import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ModalPop from 'components/sales/modal/modal';
import { DivLayoutV2FieldGrid } from 'components/UI';
import ConstantInput from 'components/sales/Input/constantInput/constantInput';
import DropDownInput from 'components/sales/Input/dropDownInput/dropDownInput';
import TextAreaInput from 'components/sales/Input/textAreaInput/textAreaInput';
import { toast } from 'react-toastify';
import DocumentUploadButton from 'services/utils/fileUpload/DocumentUploadButton';
import {
  buildFlowDataFields,
  formatDateTime,
  formatHistoryRemarks,
  formatIncentiveAmount,
  getFinalIncentiveAmount,
  getStatusLabel,
  getValidationIncentiveAmount,
} from './referralUtils';
import {
  FUSION_DOCUMENT_UPLOAD_OPTIONS,
  HAS_WALLET_REWARDS,
  LEAD_STAGE_OPTIONS,
  SITE_TYPE_OPTIONS,
} from './constants';

const MODAL_META = {
  assign: { title: 'Assign SPOC', submit: 'Assign', size: 'compact' },
  validate: { title: 'Validate Lead', submit: 'Validate', size: 'compact' },
  reject: { title: 'Reject Lead', submit: 'Reject', size: 'compact' },
  central: { title: 'Central Validate', submit: 'Submit', size: 'md' },
  stage: { title: 'Set Lead Stage', submit: 'Save', size: 'compact' },
  flow: { title: 'Flow Details', size: 'lg' },
  history: { title: 'Status History', size: 'lg' },
};

const ClientClientReferralWorkflowModal = ({
  action,
  row,
  referralFlow,
  rejectReasons = [],
  spocOptions = {},
  incentiveAmounts = null,
  getRejectReasons,
  getSpocList,
  getStatusHistory,
  assignSpoc,
  validateLead,
  rejectLead,
  centralValidate,
  setLeadStage,
  onHide,
  onSuccess,
}) => {
  const [region, setRegion] = useState(null);
  const [spoc, setSpoc] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [reason, setReason] = useState(null);
  const [selectedLeadStage, setSelectedLeadStage] = useState(null);
  const [siteType, setSiteType] = useState(null);
  const [contractNumber, setContractNumber] = useState('');
  const [contractValue, setContractValue] = useState('');
  const [b2cRevenue, setB2cRevenue] = useState('');
  const [proofUrl, setProofUrl] = useState('');
  const [invoiceUrl, setInvoiceUrl] = useState('');
  const [revenueExcelUrl, setRevenueExcelUrl] = useState('');
  const [history, setHistory] = useState(null);
  const [spocsLoaded, setSpocsLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const meta = MODAL_META[action] || MODAL_META.history;
  const isReadOnly = action === 'history' || action === 'flow';
  const validationIncentive = getValidationIncentiveAmount(incentiveAmounts);
  const finalIncentive = getFinalIncentiveAmount(contractValue, incentiveAmounts);
  const validationIncentiveLabel = formatIncentiveAmount(validationIncentive);
  const finalIncentiveLabel = formatIncentiveAmount(finalIncentive);

  const rejectReasonOptions = useMemo(
    () => rejectReasons.map((r) => ({ id: r.code, name: r.label })),
    [rejectReasons]
  );

  const { regionOptions = [], spocOptionsByRegion = {} } = spocOptions;

  const spocDropdownOptions = useMemo(
    () => (region?.id ? spocOptionsByRegion[region.id] || [] : []),
    [region?.id, spocOptionsByRegion]
  );

  const resetForm = useCallback(() => {
    setRegion(null);
    setSpoc(null);
    setRemarks('');
    setReason(null);
    setSelectedLeadStage(null);
    setSiteType(SITE_TYPE_OPTIONS[0]);
    setContractNumber('');
    setContractValue('');
    setB2cRevenue('');
    setProofUrl('');
    setInvoiceUrl('');
    setRevenueExcelUrl('');
    setHistory(null);
    setSpocsLoaded(action !== 'assign');
  }, [action]);

  useEffect(() => {
    resetForm();
  }, [action, row?.id, resetForm]);

  useEffect(() => {
    if (action !== 'assign') return;
    setSpocsLoaded(false);
    getSpocList(referralFlow)
      .then(() => setSpocsLoaded(true))
      .catch((err) => {
        toast.error(err?.message || 'Failed to load SPOCs');
        setSpocsLoaded(true);
      });
  }, [action, row?.id, getSpocList, referralFlow]);

  useEffect(() => {
    if (action !== 'reject' && action !== 'flow' && action !== 'history') return;
    getRejectReasons(referralFlow).catch((err) => {
      toast.error(err?.message || 'Failed to load reject reasons');
    });
  }, [action, row?.id, getRejectReasons, referralFlow]);

  useEffect(() => {
    if (action !== 'history' || !row?.id) return;
    setHistory(null);
    getStatusHistory(row.id)
      .then((entries) => setHistory(entries))
      .catch((err) => {
        toast.error(err.message || 'Failed to load history');
        setHistory([]);
      });
  }, [action, row?.id, getStatusHistory]);

  const handleSubmit = async () => {
    if (isReadOnly) {
      onHide();
      return;
    }
    if (!row?.id) return;

    try {
      if (action === 'assign') {
        if (!region?.id) {
          toast.warning('Region is required');
          return;
        }
        if (!spoc?.id) {
          toast.warning('SPOC is required');
          return;
        }
        setIsSubmitting(true);
        await assignSpoc(row.id, {
          region: region.id,
          assigned_spoc_email: spoc.id,
          ops_remarks: remarks.trim() || undefined,
        });
        toast.success('SPOC assigned successfully');
      } else if (action === 'validate') {
        if (!proofUrl) {
          toast.warning('Validation proof is required');
          return;
        }
        setIsSubmitting(true);
        await validateLead(row.id, {
          validation_proof_url: proofUrl,
          ops_remarks: remarks.trim() || undefined,
        });
        toast.success('Lead validated successfully');
      } else if (action === 'reject') {
        if (!reason?.id) {
          toast.warning('Rejection reason is required');
          return;
        }
        setIsSubmitting(true);
        await rejectLead(row.id, {
          rejection_reason: reason.id,
          ops_remarks: remarks.trim() || undefined,
        });
        toast.success('Lead rejected');
      } else if (action === 'central') {
        const trimmedContractNumber = contractNumber.trim();
        if (!trimmedContractNumber) {
          toast.warning('Contract number is required');
          return;
        }
        const value = Number(contractValue);
        if (!contractValue || Number.isNaN(value) || value <= 0) {
          toast.warning('Valid contract value is required');
          return;
        }
        const type = siteType?.id || 'B2B';
        if (type === 'B2B' && !invoiceUrl) {
          toast.warning('Invoice attachment is required for B2B');
          return;
        }
        if (type === 'B2C') {
          const revenue = Number(b2cRevenue);
          if (!b2cRevenue || Number.isNaN(revenue) || revenue <= 0) {
            toast.warning('B2C revenue is required');
            return;
          }
          if (!revenueExcelUrl) {
            toast.warning('Revenue excel is required for B2C');
            return;
          }
        }
        const payload = {
          contract_number: trimmedContractNumber,
          contract_value: value,
          site_type: type,
          ops_remarks: remarks.trim() || undefined,
        };
        if (type === 'B2B') {
          payload.invoice_attachment_url = invoiceUrl;
        } else {
          payload.b2c_revenue = Number(b2cRevenue);
          payload.revenue_excel_url = revenueExcelUrl;
        }
        setIsSubmitting(true);
        await centralValidate(row.id, payload);
        toast.success('Central validation submitted');
      } else if (action === 'stage') {
        if (!selectedLeadStage?.id) {
          toast.warning('Lead stage is required');
          return;
        }
        setIsSubmitting(true);
        await setLeadStage(row.id, {
          lead_stage: selectedLeadStage.id,
          ops_remarks: remarks.trim() || undefined,
        });
        toast.success('Lead stage updated');
      }

      onSuccess();
      onHide();
    } catch (err) {
      toast.error(err.message || 'Action failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderWorkflowFields = () => {
    if (action === 'assign') {
      if (!spocsLoaded) {
        return <p className="mb-0">Loading...</p>;
      }
      if (!regionOptions.length) {
        return <p className="mb-0">No SPOC regions available.</p>;
      }
      return (
        <>
          <DropDownInput
            label="Region"
            data={regionOptions}
            isStarRequired
            required
            getIdValue={(val) => {
              setRegion(val);
              setSpoc(null);
            }}
            value={region?.name || ''}
          />
          <DropDownInput
            label="SPOC"
            data={spocDropdownOptions}
            isStarRequired
            required
            disabled={!region?.id}
            getIdValue={setSpoc}
            value={spoc?.name || ''}
          />
        </>
      );
    }

    if (action === 'validate') {
      return (
        <>
          {HAS_WALLET_REWARDS && validationIncentiveLabel ? (
            <p className="site-lead-referral-disclaimer mb-3">
              Disclaimer: {validationIncentiveLabel} will be transferred to the valet&apos;s
              wallet on validation.
            </p>
          ) : null}
          <div className="site-lead-referral-upload-field">
            <DocumentUploadButton
              label="Validation Proof"
              value={proofUrl}
              onUpload={setProofUrl}
              onUploadingChange={setIsUploading}
              accept=".pdf,image/*"
              uploadOptions={FUSION_DOCUMENT_UPLOAD_OPTIONS}
              buttonLabel="Upload proof"
              uploadedLabel="✓ Uploaded · Change"
              required
            />
          </div>
        </>
      );
    }

    if (action === 'reject') {
      return (
        <DropDownInput
          label="Rejection Reason"
          data={rejectReasonOptions}
          isStarRequired
          required
          getIdValue={setReason}
          value={reason?.name || ''}
        />
      );
    }

    if (action === 'stage') {
      return (
        <DropDownInput
          label="Lead Stage"
          data={LEAD_STAGE_OPTIONS}
          isStarRequired
          required
          getIdValue={setSelectedLeadStage}
          value={selectedLeadStage?.name || ''}
        />
      );
    }

    if (action === 'central') {
      const type = siteType?.id || 'B2B';
      return (
        <>
          <DropDownInput
            label="Site Type"
            data={SITE_TYPE_OPTIONS}
            isStarRequired
            required
            getIdValue={setSiteType}
            value={siteType?.name || 'B2B'}
          />
          <ConstantInput
            label="Contract Number"
            type="text"
            value={contractNumber}
            isStarRequired
            required
            handleChange={setContractNumber}
          />
          <ConstantInput
            label="Contract Value"
            type="number"
            value={contractValue}
            isStarRequired
            required
            handleChange={setContractValue}
          />
          {HAS_WALLET_REWARDS && finalIncentiveLabel ? (
            <p className="mb-2">Final incentive: {finalIncentiveLabel}</p>
          ) : null}
          {type === 'B2B' ? (
            <div className="site-lead-referral-upload-field">
              <DocumentUploadButton
                label="Invoice Attachment"
                value={invoiceUrl}
                onUpload={setInvoiceUrl}
                onUploadingChange={setIsUploading}
                accept=".pdf,image/*"
                uploadOptions={FUSION_DOCUMENT_UPLOAD_OPTIONS}
                buttonLabel="Upload invoice"
                uploadedLabel="✓ Uploaded · Change"
                required
              />
            </div>
          ) : (
            <>
              <ConstantInput
                label="B2C Revenue"
                type="number"
                value={b2cRevenue}
                isStarRequired
                required
                handleChange={setB2cRevenue}
              />
              <div className="site-lead-referral-upload-field">
                <DocumentUploadButton
                  label="Revenue Excel"
                  value={revenueExcelUrl}
                  onUpload={setRevenueExcelUrl}
                  onUploadingChange={setIsUploading}
                  accept=".xlsx,.xls,.csv"
                  uploadOptions={FUSION_DOCUMENT_UPLOAD_OPTIONS}
                  buttonLabel="Upload excel"
                  uploadedLabel="✓ Uploaded · Change"
                  required
                />
              </div>
            </>
          )}
        </>
      );
    }

    if (action === 'flow') {
      const topLevelFields = [
        {
          key: 'referrer_project_id',
          heading: 'Referrer Project ID',
          data: row.referrer_project_id != null ? String(row.referrer_project_id) : '—',
          plainValue: true,
        },
        {
          key: 'submission_remarks',
          heading: 'Submission Remarks',
          data: row.submission_remarks || '—',
          plainValue: true,
          fullWidth: true,
        },
      ];
      return (
        <DivLayoutV2FieldGrid
          fields={[...topLevelFields, ...buildFlowDataFields(row.flow_data, rejectReasons)]}
        />
      );
    }

    if (action === 'history') {
      if (history === null) {
        return <p className="mb-0">Loading...</p>;
      }
      if (history.length === 0) {
        return <p className="mb-0">No history found.</p>;
      }
      return (
        <div className="table-responsive">
          <table className="table table-sm site-lead-referral-history-table mb-0">
            <thead>
              <tr>
                <th>From</th>
                <th>To</th>
                <th>Action</th>
                <th>By</th>
                <th>Role</th>
                <th>Remarks</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {history.map((entry) => (
                <tr key={entry.id}>
                  <td>{getStatusLabel(entry.from_status) || '—'}</td>
                  <td>{getStatusLabel(entry.to_status)}</td>
                  <td>{entry.action}</td>
                  <td>{entry.updated_by_id}</td>
                  <td>{entry.updated_by_role || '—'}</td>
                  <td>{formatHistoryRemarks(entry, rejectReasons)}</td>
                  <td>{formatDateTime(entry.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  if (!action || !row) return null;

  return (
    <ModalPop
      title={
        action === 'history' || action === 'flow'
          ? `${meta.title} — #${row.id}`
          : meta.title
      }
      show
      centered
      modalSize={meta.size}
      handleModalHide={onHide}
      footer={!isReadOnly}
      footerCloseBtn={false}
      handleModalSubmit={handleSubmit}
      footerSubmitTxt={meta.submit}
      isLoading={!isReadOnly && (isSubmitting || isUploading)}
    >
      <div className="site-lead-referral-workflow-modal">
        {renderWorkflowFields()}
        {!isReadOnly ? (
          <div className="site-lead-referral-remarks-field">
            <TextAreaInput
              label="Ops Remarks"
              rows={3}
              value={remarks}
              inputValue={remarks}
              className={remarks.trim() ? '' : 'empty'}
              handleChange={(e) => setRemarks(e.target.value)}
            />
          </div>
        ) : null}
      </div>
    </ModalPop>
  );
};

export default ClientClientReferralWorkflowModal;
